import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_HREF } from '@nrx/booking-ui';
import { Box, Container, Flex, Heading, Link, Section, Text } from '@nrx/ui';
import type { JSX, ReactNode } from 'react';

import { assertNever } from '../lib/assert-never.js';

import { isEmbedded } from './embed/embedded.js';
import type { ConsultType } from './exchange/exchange-types.js';

export type ConsultHeroVariant = 'dual' | 'ffs' | 'oneToOne' | 'group';

const CHECK_ICON =
  'https://cdn.prod.website-files.com/619945d6e2622010be987644/687677d17dbf233d38692db3_ic_baseline-check-circle-red.svg';

/** Fee-for-service landing: hero headline and body (1:1-only scheduling). */
const FFS_HERO_SUBHEADING =
  'Get dedicated time with a nutrition coach to talk through your goals, ask questions, and get personalized tips that fit your life. Select the sessions that fit your schedule and take a step closer to reaching your health goals!';

/** Non-FFS 1:1-only tab: hero subheading (no group option). */
const ONE_TO_ONE_HERO_SUBHEADING =
  'Select the sessions that fit your health goals and learn in 30‑minute live consults with individualized support from a dietitian below!';

/** Group-only tab: hero subheading. */
const GROUP_HERO_SUBHEADING =
  'Select the sessions that fit your health goals and learn in 30‑minute live group consults below!';

/** Decorative hero collage, shown from the `lg` breakpoint up (see `.consult-hero__collage`). */
const HERO_COLLAGE = [
  {
    src: 'https://cdn.prod.website-files.com/619945d6e2622010be987644/688d0f934619323e4d385772_shutterstock_2132665973%201.png',
    className: 'consult-hero__collage-image consult-hero__collage-image--top',
  },
  {
    src: 'https://cdn.prod.website-files.com/619945d6e2622010be987644/688d0f921a5555b4b9c68a46_shutterstock_1694685292%201.png',
    className: 'consult-hero__collage-image consult-hero__collage-image--bottom',
  },
] as const;

/**
 * Hero headline/body variant aligned with `getVisibleTabs`: dual messaging only when both
 * scheduling modes are offered (`flexible` or unknown exchange type).
 *
 * @remarks Ends in {@link assertNever} for the same reason as `getVisibleTabs`: the trailing
 * `return 'dual'` it replaces was unreachable for every declared input and was what would let a new
 * `ConsultType` render dual-mode hero copy with no signal.
 */
export function consultHeroVariant(consultType: ConsultType | undefined): ConsultHeroVariant {
  if (consultType === 'ffs') return 'ffs';
  if (consultType === undefined || consultType === 'flexible') return 'dual';
  if (consultType === '1:1') return 'oneToOne';
  if (consultType === 'group') return 'group';
  return assertNever(consultType);
}

/**
 * Wraps consult scheduling content with the top hero (headline + intro).
 *
 * @param heroVariant - `dual`: both group and 1:1 tabs. `oneToOne` / `group`: single-mode copy.
 *   `ffs`: fee-for-service 1:1-only copy.
 *
 * @remarks Both bands are `Container size="4"` — the same 1136px preset as the shell header and
 * footer — so the brand lockup lines up with the content edge on wide viewports. The vendor
 * hardcoded 1080px here, which left the logo 28px outside the copy column.
 *
 * The rust emphasis inside the headlines is a bare `<span>` carrying an app-scoped token class,
 * not a `Text` with a colour `className`: re-specifying colour through `className` on a library
 * component is a W3 violation and `Text` exposes no accent colour. The token is
 * `--color-on-accent-soft` (#b93932, 5.68:1 on white), not deck coral, which is 3.58:1 and fails
 * AA at this size.
 *
 * Inside care-betty's booking modal (`?embedded=1`) only the scheduling body renders: the hero, the
 * "Sign Up" heading and the support contact are member-facing copy, and the modal's own title says
 * who the booking is for.
 */
export function ConsultContentShell({
  children,
  heroVariant,
}: {
  children: ReactNode;
  heroVariant: ConsultHeroVariant;
}): JSX.Element {
  if (isEmbedded()) {
    return (
      <Box className="consult-schedule consult-schedule--embedded">
        <Container size="4">{children}</Container>
      </Box>
    );
  }

  const heroIntro = (() => {
    if (heroVariant === 'ffs') {
      return (
        <>
          <Heading as="h1" size="8" className="consult-hero__headline">
            <span className="consult-hero__line">
              Meet <span className="consult-accent">one-on-one</span>
            </span>
            <span className="consult-hero__line">with a nutrition coach</span>
          </Heading>
          <Text as="p" size="4" className="consult-hero__lede">
            {FFS_HERO_SUBHEADING}
          </Text>
        </>
      );
    }
    if (heroVariant === 'dual') {
      return (
        <>
          <Heading as="h1" size="8" className="consult-hero__headline">
            Meet <span className="consult-accent">1:1</span> with an RDN or Learn Together in a{' '}
            <span className="consult-accent">Live Group</span> Consult
          </Heading>
          <Text as="p" size="4" color="muted" className="consult-hero__lede">
            Select the sessions that fit your health goals and learn in 30‑minute live consults.
            Choose between learning in a group setting or receiving individualized support from a
            dietitian below!
          </Text>
        </>
      );
    }
    if (heroVariant === 'oneToOne') {
      return (
        <>
          <Heading as="h1" size="8" className="consult-hero__headline">
            Meet <span className="consult-accent">1:1</span> with an RDN
          </Heading>
          <Text as="p" size="4" color="muted" className="consult-hero__lede">
            {ONE_TO_ONE_HERO_SUBHEADING}
          </Text>
        </>
      );
    }
    return (
      <>
        <Heading as="h1" size="8" className="consult-hero__headline">
          Learn Together in a <span className="consult-accent">Live Group</span> Consult
        </Heading>
        <Text as="p" size="4" color="muted" className="consult-hero__lede">
          {GROUP_HERO_SUBHEADING}
        </Text>
      </>
    );
  })();

  return (
    <Box>
      <Section size="4" className="consult-hero">
        <Container size="4">
          <Flex className="consult-hero__row">
            <Box className="consult-hero__copy">
              {heroIntro}
              <Flex direction="column" gap="4">
                {['Connect', 'Learn', 'Take Action'].map((label) => (
                  <Flex key={label} align="center" gap="3">
                    <img
                      src={CHECK_ICON}
                      alt=""
                      width={24}
                      height={24}
                      className="consult-hero__check"
                    />
                    <Text weight="medium">{label}</Text>
                  </Flex>
                ))}
              </Flex>
            </Box>
            <Box className="consult-hero__collage" aria-hidden>
              {HERO_COLLAGE.map(({ src, className }) => (
                <img key={src} src={src} alt="" className={className} loading="lazy" />
              ))}
            </Box>
          </Flex>
        </Container>
      </Section>

      <Section size="4" className="consult-schedule">
        <Container size="4">
          <Heading as="h2" size="7" className="consult-schedule__title">
            Sign Up For Your Next Session
          </Heading>
          <Text as="p" align="center" color="muted" className="consult-schedule__intro">
            Have a question? You can text or call NourishedRx at{' '}
            <Link href={SUPPORT_PHONE_HREF} underline>
              <Text weight="medium">{SUPPORT_PHONE_DISPLAY}</Text>
            </Link>{' '}
            or email us at{' '}
            <Link href="mailto:food@nourishedrx.com" underline>
              <Text weight="medium">food@nourishedrx.com</Text>
            </Link>
            . We&apos;re available Monday to Friday, 8 a.m. to 8 p.m. ET to help you get started.
          </Text>
          {children}
        </Container>
      </Section>
    </Box>
  );
}
